import { AccordionContent, AccordionItem, AccordionTrigger, Spinner } from '@ibsys/kit';
import { FileCheck2Icon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useExpertReport, type BlockResponse } from '@/entities/expert-report';
import { TreeBlockNode } from './TreeBlockNode';
import type { RuleWithPath } from './types';

type ReportSectionProps = {
  reportId: number;
  reportName: string;
  isExpanded: boolean;
  search: string;
  linkedSet: Set<number>;
  selectedRules: Map<number, RuleWithPath>;
  onToggleRule: (rule: RuleWithPath) => void;
};

/** Проверяет наличие совпадающих правил в блоках */
const blocksHaveMatchingRules = (blocks: BlockResponse[], search: string): boolean => {
  for (const block of blocks) {
    if (block.isDeleted) continue;
    for (const rule of block.rules) {
      if (rule.isDeleted) continue;
      if (rule.name.toLowerCase().includes(search) || rule.text.toLowerCase().includes(search)) {
        return true;
      }
    }
    if (blocksHaveMatchingRules(block.children, search)) return true;
  }
  return false;
};

export const ReportSection: FC<ReportSectionProps> = ({
  reportId,
  reportName,
  isExpanded,
  search,
  linkedSet,
  selectedRules,
  onToggleRule,
}) => {
  const { data: reportDetail, isLoading } = useExpertReport(isExpanded ? reportId : undefined);

  const searchLower = search.toLowerCase().trim();

  const hasMatchingRules = useMemo(() => {
    if (!searchLower) return true;
    if (reportName.toLowerCase().includes(searchLower)) return true;
    if (!reportDetail?.blocks) return false;
    return blocksHaveMatchingRules(reportDetail.blocks, searchLower);
  }, [reportDetail?.blocks, reportName, searchLower]);

  if (searchLower && !hasMatchingRules) {
    return null;
  }

  return (
    <AccordionItem value={String(reportId)} className="border-0">
      <AccordionTrigger iconSide="left" className="px-2 py-1.5 hover:bg-muted/50 rounded-md">
        <div className="flex items-center gap-2">
          <FileCheck2Icon size={16} className="shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium">{reportName}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-0 pb-0">
        {isLoading && (
          <div className="flex justify-center py-4">
            <Spinner className="size-5" />
          </div>
        )}
        {reportDetail?.blocks && (
          <div className="flex flex-col">
            {reportDetail.blocks
              .filter((b) => !b.isDeleted)
              .map((block) => (
                <TreeBlockNode
                  key={block.id}
                  block={block}
                  reportId={reportId}
                  reportName={reportName}
                  depth={0}
                  blockPath={[]}
                  search={searchLower}
                  linkedSet={linkedSet}
                  selectedRules={selectedRules}
                  onToggleRule={onToggleRule}
                />
              ))}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
